import { useMemo, useState } from 'react'
import { Chip } from '@/components/commons/chip'
import { GalleryModal } from '@/components/commons/gallery-modal'
import { GalleryModalHeader } from '@/components/commons/gallery-modal-header'
import { GalleryModalThumbnails } from '@/components/commons/gallery-modal-thumbnails'
import { AlertTriangleIcon, CheckIcon } from '@/components/ui/icons'
import { Separator } from '@/components/ui/separator'
import type { ExperienceGalleryImage } from '@/features/experience-shell/types'
import type { RemapExperienceVetting } from '@/features/experience-vetting/types.ts'
import {
	OpsVettingDisabledContainer,
	OpsVettingGalleryGrid,
	OpsVettingParagraph,
	OpsVettingSectionTitle,
	OpsVettingSectionWrapper
} from '@/features/ops-vetting/components/ops-vetting-atoms'
import { useGalleryNeedsReviewShortcuts } from '@/features/photoset/hooks/use-gallery-needs-review-shortcuts'
import { useExperienceVettingError } from '@/features/service-vetting/store/use-service-vetting-error'
import { useTranslation } from '@/translations/i18n.tsx'
import { ExperienceVettingCriteriaRejectedImageItem } from './experience-vetting-criteria-rejected-image-item'

const StatusMessage = ({
	pendingImagesCount
}: {
	pendingImagesCount: number
}) => {
	const { t } = useTranslation()
	if (pendingImagesCount > 0) {
		return t(
			'vetting:criteria.commons.rejected_images.images_remaining_flags',
			{
				count: pendingImagesCount
			}
		)
	}

	return (
		<span className='flex gap-1'>
			<CheckIcon className='fill-success-600' size={20} />
			{t(
				'vetting:criteria.commons.rejected_images.images_remaining_no_flags'
			)}
		</span>
	)
}

export const ExperienceVettingCriteriaRejectedImages = ({
	experience,
	isReadOnly
}: {
	experience: RemapExperienceVetting
	isReadOnly: boolean
}) => {
	const { t } = useTranslation()
	const { gallery } = experience
	const isDeclined = experience.expertise.status === 'declined'

	const [expandedImageIndex, setExpandedImageIndex] = useState<number | null>(
		null
	)
	const [showGalleryModal, setShowGalleryModal] = useState(false)

	const errors = useExperienceVettingError((state) => state.errors)
	const filteredGalleryImages = useMemo(
		() =>
			gallery.images.filter(
				(image) =>
					!(image.isCover || image.isItineraryImage) &&
					image.isApproved === false
			),
		[gallery]
	)

	const pendingImagesCount = useMemo(
		() =>
			filteredGalleryImages.filter((image) => image.flags.length === 0)
				.length,
		[filteredGalleryImages]
	)

	const handleClickExpandedImage = (
		imageId: ExperienceGalleryImage['id']
	) => {
		const index = filteredGalleryImages.findIndex(
			(image) => image.id === imageId
		)
		setExpandedImageIndex(index)
		setShowGalleryModal(true)
	}

	const handleCloseModal = () => {
		setExpandedImageIndex(null)
		setShowGalleryModal(false)
	}

	useGalleryNeedsReviewShortcuts({
		selectableImages: filteredGalleryImages,
		currentDetailImageIndex: expandedImageIndex || 0,
		setCurrentDetailImageIndex: setExpandedImageIndex,
		onClose: handleCloseModal
	})

	return (
		<OpsVettingSectionWrapper direction='column' fullWidth gap={6}>
			<div className='flex w-full items-center justify-between'>
				<OpsVettingSectionTitle>
					{t('vetting:criteria.commons.rejected_images.title')}
				</OpsVettingSectionTitle>
				<div className='flex items-center gap-4'>
					{errors.rejectedImagesErrors.map((error) => (
						<Chip
							isActive
							key={error}
							label={t(error)}
							startSlot={<AlertTriangleIcon />}
							variant='danger'
						/>
					))}

					<OpsVettingParagraph>
						<StatusMessage
							pendingImagesCount={pendingImagesCount}
						/>
					</OpsVettingParagraph>
				</div>
			</div>

			<Separator orientation='horizontal' />

			<OpsVettingDisabledContainer $isDisabled={isDeclined}>
				<OpsVettingGalleryGrid>
					{filteredGalleryImages.map((image) => (
						<ExperienceVettingCriteriaRejectedImageItem
							experienceId={experience.id}
							galleryId={gallery.id}
							hideAction={isReadOnly}
							image={image}
							key={image.id}
							onClickExpand={() =>
								handleClickExpandedImage(image.id)
							}
						/>
					))}
				</OpsVettingGalleryGrid>
			</OpsVettingDisabledContainer>

			{showGalleryModal && expandedImageIndex !== null && (
				<GalleryModal
					currentImage={filteredGalleryImages[expandedImageIndex]}
					headerSlot={
						<GalleryModalHeader
							currentImage={
								filteredGalleryImages[expandedImageIndex]
							}
							imagesCount={filteredGalleryImages.length}
							onClose={handleCloseModal}
						/>
					}
					onClose={handleCloseModal}
					thumbnails={{
						l: filteredGalleryImages[expandedImageIndex].url || '',
						m: '',
						s: '',
						xs: ''
					}}
					thumbnailsSlot={
						<GalleryModalThumbnails
							currentImageId={
								filteredGalleryImages[expandedImageIndex].id
							}
							images={filteredGalleryImages.map((image) => ({
								id: image.id,
								name: image.name,
								thumbnails: {
									l: image.url || '',
									m: image.url || '',
									s: image.url || '',
									xs: image.url || ''
								}
							}))}
							onSelect={(_, index) => {
								setExpandedImageIndex(index)
							}}
							thumbnails='thumbnails'
						/>
					}
				/>
			)}
		</OpsVettingSectionWrapper>
	)
}
